
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { useTranslation } from '../../../TranslationContext';

interface ApplicationFileUploadProps {
  onFileChange: (file: File | null, roles: string[] | null, error?: string) => void;
}

const ApplicationFileUpload: React.FC<ApplicationFileUploadProps> = ({ onFileChange }) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Télécharge un template CSV sécurisé
   */
  const downloadTemplate = () => {
    const csvContent = "RoleName\nadmin\nuser\nmanager\neditor";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, t('template_filename'));
  };

  /**
   * Vérifie la sécurité du fichier
   * @param file Fichier à analyser
   * @returns true si le fichier est sûr
   */
  const checkFileSafety = (file: File): boolean => {
    const unsafePatterns = [
      '=cmd|',       // Commandes Windows
      '<?php',       // Code PHP
      '<script',     // JavaScript
      'Macros',      // Indice de macros
      'VBA',         // Visual Basic
      'eval(',       // Fonction dangereuse
      'ActiveX',     // Composant risqué
      '\\x',         // Encodage suspect
      'powershell',  // Commandes PowerShell
      '/bin/bash'    // Commandes bash
    ];
    
    // Vérification du nom et du type MIME
    if (unsafePatterns.some(pattern => file.name.includes(pattern))) {
      return false;
    }

    // Vérification de la taille (max 1MB)
    if (file.size > 1_000_000) {
      return false;
    }

    return true;
  };

  /**
   * Valide le contenu du fichier CSV
   */
  const validateCsv = (file: File, callback: (success: boolean, roles?: string[], error?: string) => void) => {
    if (!checkFileSafety(file)) {
      callback(false, undefined, t('error_security_risk'));
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as { RoleName?: string }[];
        
        // Vérification de la structure
        if (!data[0] || !('RoleName' in data[0])) {
          callback(false, undefined, t('error_invalid_csv_structure'));
          return;
        }

        // Extraction et validation des rôles
        const roles = data
          .map(row => row.RoleName)
          .filter(Boolean)
          .filter(role => {
            // Validation supplémentaire des rôles
            return typeof role === 'string' && 
                   role.length <= 50 && 
                   /^[a-zA-Z0-9_-]+$/.test(role);
          }) as string[];
        
        if (roles.length === 0) {
          callback(false, undefined, t('error_no_valid_roles'));
          return;
        }

        callback(true, roles);
      },
      error: () => {
        callback(false, undefined, t('error_parsing_csv'));
      }
    });
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      onFileChange(null, null, t('error_no_file_selected'));
      return;
    }

    const file = acceptedFiles[0];
    
    // Vérification de l'extension
    if (!file.name.toLowerCase().endsWith('.csv')) {
      onFileChange(null, null, t('error_only_csv'));
      return;
    }

    // Vérification du type MIME
    if (!['text/csv', 'application/vnd.ms-excel'].includes(file.type) && file.type !== '') {
      onFileChange(null, null, t('error_invalid_file_type'));
      return;
    }

    setIsProcessing(true);
    
    // Validation du contenu
    validateCsv(file, (success, roles, error) => {
      setIsProcessing(false);
      if (success && roles) {
        onFileChange(file, roles);
      } else {
        onFileChange(null, null, error || t('error_invalid_csv'));
      }
    });
  }, [onFileChange, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    maxSize: 1_000_000, // 1MB max
    noClick: false,
    noKeyboard: false
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        } ${isProcessing ? 'opacity-50' : ''}`}
      >
        <input {...getInputProps()} />
        {isProcessing ? (
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-5 w-5 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm text-gray-600">{t('scanning_file')}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {isDragActive
              ? t('drop_csv')
              : t('drag_drop_csv')}
          </p>
        )}
      </div>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={downloadTemplate}
          className="text-sm text-indigo-600 hover:text-indigo-500"
          aria-label={t('download_template')}
        >
          {t('download_template')}
        </button>
      </div>
      <p className="text-xs text-gray-500 text-center">
        {t('file_constraints')}
      </p>
    </div>
  );
};

export default ApplicationFileUpload;

// import React, { useCallback, useState } from 'react';
// import { useDropzone } from 'react-dropzone';
// import { saveAs } from 'file-saver';
// import Papa from 'papaparse';

// interface ApplicationFileUploadProps {
//   onFileChange: (file: File | null, roles: string[] | null, error?: string) => void;
// }

// const ApplicationFileUpload: React.FC<ApplicationFileUploadProps> = ({ onFileChange }) => {
//   const [isProcessing, setIsProcessing] = useState(false);

//   /**
//    * Télécharge un template CSV sécurisé
//    */
//   const downloadTemplate = () => {
//     const csvContent = "RoleName\nadmin\nuser\nmanager\neditor";
//     const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
//     saveAs(blob, 'application_roles_template.csv');
//   };

//   /**
//    * Vérifie la sécurité du fichier
//    * @param file Fichier à analyser
//    * @returns true si le fichier est sûr
//    */
//   const checkFileSafety = (file: File): boolean => {
//     const unsafePatterns = [
//       '=cmd|',       // Commandes Windows
//       '<?php',       // Code PHP
//       '<script',     // JavaScript
//       'Macros',      // Indice de macros
//       'VBA',         // Visual Basic
//       'eval(',       // Fonction dangereuse
//       'ActiveX',     // Composant risqué
//       '\\x',         // Encodage suspect
//       'powershell',  // Commandes PowerShell
//       '/bin/bash'    // Commandes bash
//     ];
    
//     // Vérification du nom et du type MIME
//     if (unsafePatterns.some(pattern => file.name.includes(pattern))) {
//       return false;
//     }

//     // Vérification de la taille (max 1MB)
//     if (file.size > 1_000_000) {
//       return false;
//     }

//     return true;
//   };

//   /**
//    * Valide le contenu du fichier CSV
//    */
//   const validateCsv = (file: File, callback: (success: boolean, roles?: string[], error?: string) => void) => {
//     if (!checkFileSafety(file)) {
//       callback(false, undefined, 'File rejected: potential security risk detected');
//       return;
//     }

//     Papa.parse(file, {
//       header: true,
//       skipEmptyLines: true,
//       complete: (results) => {
//         const data = results.data as { RoleName?: string }[];
        
//         // Vérification de la structure
//         if (!data[0] || !('RoleName' in data[0])) {
//           callback(false, undefined, 'Invalid CSV structure. Must contain "Role" column');
//           return;
//         }

//         // Extraction et validation des rôles
//         const roles = data
//           .map(row => row.RoleName)
//           .filter(Boolean)
//           .filter(role => {
//             // Validation supplémentaire des rôles
//             return typeof role === 'string' && 
//                    role.length <= 50 && 
//                    /^[a-zA-Z0-9_-]+$/.test(role);
//           }) as string[];
        
//         if (roles.length === 0) {
//           callback(false, undefined, 'No valid roles found in CSV file');
//           return;
//         }

//         callback(true, roles);
//       },
//       error: () => {
//         callback(false, undefined, 'Error parsing CSV file');
//       }
//     });
//   };

//   const onDrop = useCallback((acceptedFiles: File[]) => {
//     if (acceptedFiles.length === 0) {
//       onFileChange(null, null, 'No file selected');
//       return;
//     }

//     const file = acceptedFiles[0];
    
//     // Vérification de l'extension
//     if (!file.name.toLowerCase().endsWith('.csv')) {
//       onFileChange(null, null, 'Only CSV files are accepted');
//       return;
//     }

//     // Vérification du type MIME
//     if (!['text/csv', 'application/vnd.ms-excel'].includes(file.type) && file.type !== '') {
//       onFileChange(null, null, 'Invalid file type');
//       return;
//     }

//     setIsProcessing(true);
    
//     // Validation du contenu
//     validateCsv(file, (success, roles, error) => {
//       setIsProcessing(false);
//       if (success && roles) {
//         onFileChange(file, roles);
//       } else {
//         onFileChange(null, null, error || 'Invalid CSV file');
//       }
//     });
//   }, [onFileChange]);

//   const { getRootProps, getInputProps, isDragActive } = useDropzone({
//     onDrop,
//     accept: {
//       'text/csv': ['.csv']
//     },
//     maxFiles: 1,
//     maxSize: 1_000_000, // 1MB max
//     noClick: false,
//     noKeyboard: false
//   });

//   return (
//     <div className="space-y-2">
//       <div
//         {...getRootProps()}
//         className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${
//           isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
//         } ${isProcessing ? 'opacity-50' : ''}`}
//       >
//         <input {...getInputProps()} />
//         {isProcessing ? (
//           <div className="flex flex-col items-center">
//             <svg className="animate-spin h-5 w-5 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//             </svg>
//             <p className="text-sm text-gray-600">Scanning file for security...</p>
//           </div>
//         ) : (
//           <p className="text-sm text-gray-600">
//             {isDragActive
//               ? 'Drop the CSV file here...'
//               : 'Drag and drop a CSV file here, or click to select'}
//           </p>
//         )}
//       </div>

//       <div className="flex justify-center">
//         <button
//           type="button"
//           onClick={downloadTemplate}
//           className="text-sm text-indigo-600 hover:text-indigo-500"
//         >
//           Download Template
//         </button>
//       </div>
//       <p className="text-xs text-gray-500 text-center">
//         Max file size: 1MB • Only plain CSV files accepted
//       </p>
//     </div>
//   );
// };

// export default ApplicationFileUpload;